import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { SovosBox, SovosTypography } from "mosaic-react";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { KPI_BORDER_COLORS } from "../constants";
import ColumnMappingAiLoadingBanner from "../components/ColumnMappingAiLoadingBanner";
import ColumnMappingAiResultBanner from "../components/ColumnMappingAiResultBanner";
import MappingDropdown from "../components/MappingDropdown";
import MappingGridToolbar from "../components/MappingGridToolbar";
import StatTile from "../components/StatTile";
import { toFieldDisplayLabel, toSourceColumnLabel } from "../../mappingDisplayLabels";

const HIGH_CONFIDENCE_THRESHOLD = 0.8;

const StepMapColumns = ({
    needsMappingCount,
    readyCount,
    skippedCount,
    totalColumns,
    mappings,
    mschFields,
    usedFields,
    aiSuggestedColumns,
    aiSuggestionMeta,
    aiAppliedSkippedCsv,
    loadingAiMapping,
    onCancelAiMapping,
    originalMappingsRef,
    hideDoNotImport,
    showDoNotImportToggle,
    dgItems,
    dgHeader,
    dgAvailableFilters,
    i18n,
    entityLabel,
    onChangeEntity,
    loading,
    onMappingChange,
    onRevert,
    onToggleHideDoNotImport,
}) => {
    const gridSectionRef = useRef(null);
    const [reviewSuggestionColumn, setReviewSuggestionColumn] = useState(null);
    const [reviewSuggestionSignal, setReviewSuggestionSignal] = useState(0);
    const [showAiSuggestions, setShowAiSuggestions] = useState(false);

    const hasAiSuggestions = aiSuggestedColumns.size > 0;

    useEffect(() => {
        if (hasAiSuggestions) {
            setShowAiSuggestions(true);
        }
    }, [hasAiSuggestions]);

    const highConfidenceSuggestions = useMemo(
        () =>
            [...aiSuggestionMeta.entries()]
                .filter(
                    ([column, meta]) =>
                        (meta.score ?? 0) > HIGH_CONFIDENCE_THRESHOLD &&
                        mappings[column] === meta.gridId,
                )
                .map(([column, meta]) => ({
                    column,
                    columnLabel: toSourceColumnLabel(column),
                    score: meta.score ?? 0,
                    targetLabel: toFieldDisplayLabel(meta.gridId, mschFields, meta.displayName),
                })),
        [aiSuggestionMeta, mappings, mschFields],
    );
    const highConfidenceSuggestionCount = highConfidenceSuggestions.length;

    const columnHeaderRenderer = useCallback(
        (headerItem) => {
            const csvColumn = headerItem.columnName;
            const displayName = toSourceColumnLabel(headerItem.headerName || csvColumn);
            const value = mappings[csvColumn] ?? null;
            const suggestionMeta = aiSuggestionMeta.get(csvColumn);
            const aiScoreValue = aiSuggestedColumns.get(csvColumn);
            const isAiSuggested =
                showAiSuggestions &&
                aiScoreValue != null &&
                suggestionMeta?.gridId === value;

            return (
                <SovosBox sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5, gap: 0.5 }}>
                    <SovosTypography variant="caption" fontWeight={600} color="text.primary" noWrap>
                        {displayName}
                    </SovosTypography>
                    <MappingDropdown
                        csvColumn={csvColumn}
                        displayName={displayName}
                        value={value}
                        originalValue={originalMappingsRef.current[csvColumn] ?? null}
                        mschFields={mschFields}
                        usedFields={usedFields}
                        aiScore={isAiSuggested ? aiScoreValue : null}
                        suggestionMeta={isAiSuggested ? suggestionMeta : null}
                        onChange={onMappingChange}
                        onRevert={onRevert}
                        entityLabel={entityLabel}
                        onChangeEntity={onChangeEntity}
                        reviewSignal={reviewSuggestionColumn === csvColumn ? reviewSuggestionSignal : 0}
                        i18n={i18n}
                    />
                </SovosBox>
            );
        },
        [
            aiSuggestedColumns,
            aiSuggestionMeta,
            entityLabel,
            i18n,
            mappings,
            mschFields,
            onChangeEntity,
            onMappingChange,
            onRevert,
            originalMappingsRef,
            reviewSuggestionColumn,
            reviewSuggestionSignal,
            showAiSuggestions,
            usedFields,
        ],
    );

    const handleReviewSuggestion = (column) => {
        setShowAiSuggestions(true);
        if (column) {
            setReviewSuggestionColumn(column);
            setReviewSuggestionSignal((signal) => signal + 1);
        }
        gridSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (loading) {
        return (
            <SovosBox
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    minHeight: "calc(100vh - 280px)",
                }}
            >
                <CircularProgress size={28} />
                <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                    {i18n`Loading mapping data…`}
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <>
            {loadingAiMapping && (
                <ColumnMappingAiLoadingBanner i18n={i18n} onCancel={onCancelAiMapping} />
            )}

            {!loadingAiMapping && highConfidenceSuggestionCount > 0 && (
                <ColumnMappingAiResultBanner
                    suggestionCount={highConfidenceSuggestionCount}
                    suggestions={highConfidenceSuggestions}
                    onReviewSuggestion={handleReviewSuggestion}
                    i18n={i18n}
                />
            )}

            {!loadingAiMapping && aiAppliedSkippedCsv > 0 && (
                <SovosTypography variant="caption" color="warning.dark" sx={{ display: "block", mb: 2 }}>
                    {i18n`${aiAppliedSkippedCsv} proposed rows were not applied because their CSV column names did not match this file.`}
                </SovosTypography>
            )}

            <SovosBox
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mb: 3,
                }}
            >
                <StatTile label={i18n`Columns need to be mapped`} value={needsMappingCount} borderColor={KPI_BORDER_COLORS[0]} />
                <StatTile label={i18n`Ready for next step`} value={readyCount} borderColor={KPI_BORDER_COLORS[1]} />
                <StatTile label={i18n`Columns skipped mapping`} value={skippedCount} borderColor={KPI_BORDER_COLORS[2]} />
                <StatTile label={i18n`All columns`} value={totalColumns} borderColor={KPI_BORDER_COLORS[3]} />
            </SovosBox>

            <SovosBox ref={gridSectionRef} sx={{ maxWidth: "100%", minWidth: 0, overflowX: "auto", width: "100%" }}>
                <DataGrid
                    key={`column-mapping-grid-${hideDoNotImport}-${showAiSuggestions}`}
                    id="column-mapping-grid"
                    items={dgItems}
                    header={dgHeader}
                    card={rows.CellCard}
                    status={{ loading: false }}
                    metadata={{ total: dgItems.length }}
                    enablePagination
                    headerHeight={80}
                    columnHeaderRenderer={columnHeaderRenderer}
                    availableFilters={dgAvailableFilters}
                    toolbar={MappingGridToolbar}
                    toolbarProps={{
                        entityLabel,
                        hasAiSuggestions,
                        hideDoNotImport,
                        onToggleHideDoNotImport,
                        onToggleShowAiSuggestions: () => setShowAiSuggestions((visible) => !visible),
                        showAiSuggestionToggle: true,
                        showAiSuggestions,
                        showDoNotImportToggle,
                    }}
                    showCheckbox={false}
                    modifierssx={{
                        "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                        "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                        "& .MuiDataGrid-columnHeaderTitleContainer": { alignItems: "flex-start" },
                        "& .MuiDataGrid-columnHeaderTitleContainerContent": { width: "100%" },
                        "& .MuiDataGrid-main": { minWidth: 0, overflowX: "auto" },
                        "& .MuiDataGrid-virtualScroller": { overflowX: "auto" },
                    }}
                />
            </SovosBox>
        </>
    );
};

export default StepMapColumns;
